import { useState } from "react";
import {
  IconPencil,
  IconCheck,
  IconLockPassword,
  IconCalendar,
} from "@tabler/icons-react";
import styles from "../../../../pages/ClientDashboard/Profile/profile.module.css";
import Button from "../../Button/button";
import PasswordField from "../../Password/PasswordField";
import PasswordStrengthMeter from "../../Password/PasswordStrengthMeter";
import { clientChangePassword } from "../../../../services/clientCalls";
import { errorHandler } from "../../../../utils";

const EMPTY_PW_FORM = {
  password: "",
  newPassword: "",
  confirmPassword: "",
};

export default function ChangePasswordCard({ onShowToast }) {
  const [editing, setEditing] = useState(false);
  const [load, setLoad] = useState(false);
  const [form, setForm] = useState(EMPTY_PW_FORM);

  function startEdit() {
    setForm(EMPTY_PW_FORM);
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
  }

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setLoad(true);
      const res = await clientChangePassword(form);
      if (res?.data?.success) {
        setForm(EMPTY_PW_FORM);
        onShowToast("Password changed successfully");
      }
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong, Please check your internet connection and try again.",
            "Error",
          );
    } finally {
      setEditing(false);
      setLoad(false);
    }
  };

  const setField = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div className={styles.cardHeaderLeft}>
          <div className={styles.cardIcon}>
            <IconLockPassword size={18} />
          </div>
          <div>
            <div className={styles.cardTitle}>Change Password</div>
            <div className={styles.cardSubtitle}>
              Use a strong, unique password
            </div>
          </div>
        </div>
        <div className={styles.btnGroup}>
          {!editing && (
            <Button
              variant="primary"
              size="sm"
              leftIcon={<IconPencil size={14} />}
              onClick={startEdit}
            >
              Edit
            </Button>
          )}
        </div>
      </div>

      <div className={styles.cardBody}>
        {!editing ? (
          <>
            <div className={styles.infoField}>
              <div className={styles.infoLabel}>Password</div>
              <div
                className={styles.infoValue}
                style={{ letterSpacing: 4, fontSize: 18 }}
              >
                ••••••••••••
              </div>
            </div>
            <p
              style={{
                fontSize: 12,
                color: "var(--text-muted)",
                marginTop: 10,
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <IconCalendar size={14} /> Last changed 45 days ago
            </p>
          </>
        ) : (
          <div className={styles.fieldGroup}>
            <PasswordField
              id="e-oldPw"
              label="Current Password"
              placeholder="Enter current password"
              value={form.password}
              onChange={setField("password")}
            />
            <PasswordField
              id="e-newPw"
              label="New Password"
              placeholder="At least 8 characters"
              value={form.newPassword}
              onChange={setField("newPassword")}
              hint={<PasswordStrengthMeter value={form.newPassword} />}
            />
            <PasswordField
              id="e-confirmPw"
              label="Confirm New Password"
              placeholder="Repeat new password"
              value={form.confirmPassword}
              onChange={setField("confirmPassword")}
            />
            <div className={styles.editActions}>
              <Button
                variant="secondary"
                leftIcon={<IconCheck size={15} />}
                onClick={handleSave}
                loading={load}
                disabled={
                  load ||
                  !form.password ||
                  !form.newPassword ||
                  !form.confirmPassword
                }
              >
                Update Password
              </Button>
              <Button variant="outlineBlue" onClick={cancelEdit}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
